import {
  Dialog,
  Form,
  FormButton,
  Input,
  useNotifications
} from '@ijoin/components';
import {
  GetEnrollmentAnnuityPolicyRes,
  getGetEnrollmentAnnuityPoliciesQueryKey,
  usePatchEnrollmentAnnuityPolicy
} from '@ijoin/hooks';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { FC } from 'react';
import { useForm } from 'react-hook-form';

import { ApiError } from '../types';

type FormValues = {
  annuityRothAccountId: string;
  annuityTraditionalAccountId: string;
};

export const EnrollmentAnnuityPolicyEditDialog: FC<{
  onClose: () => void;
  planEnrollmentId: string;
  planId: string;
  policy: GetEnrollmentAnnuityPolicyRes;
}> = props => {
  const queryClient = useQueryClient();
  const notifications = useNotifications();

  const methods = useForm<FormValues>({
    defaultValues: {
      annuityRothAccountId: props.policy.annuityRothAccountId ?? '',
      annuityTraditionalAccountId:
        props.policy.annuityTraditionalAccountId ?? ''
    }
  });

  const patchPolicy = usePatchEnrollmentAnnuityPolicy<ApiError>({
    mutation: {
      onError: error =>
        notifications.error(error.message || 'Failed to update annuity policy'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetEnrollmentAnnuityPoliciesQueryKey(
            props.planId,
            props.planEnrollmentId
          )
        });
        notifications.success('Annuity policy updated');
        props.onClose();
      }
    }
  });

  const onSubmit = (data: FormValues) =>
    patchPolicy.mutate({
      annuityPolicyParticipantId: props.policy.id,
      data: {
        annuityRothAccountId: data.annuityRothAccountId.trim() || null,
        annuityTraditionalAccountId:
          data.annuityTraditionalAccountId.trim() || null
      },
      planEnrollmentId: props.planEnrollmentId,
      planId: props.planId
    });

  return (
    <Dialog
      data-testid='editAnnuityPolicyDialog'
      fullWidth
      isOpen
      maxWidth='sm'
      onClose={props.onClose}>
      <Form methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
        <DialogTitle variant='h3'>
          {`Edit ${props.policy.annuityAccountType} Annuity Policy`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Input
              data-testid='annuityTraditionalAccountId'
              format='string'
              fullWidth
              label='Traditional Account ID'
              name='annuityTraditionalAccountId'
              size='small'
            />
            <Input
              data-testid='annuityRothAccountId'
              format='string'
              fullWidth
              label='Roth Account ID'
              name='annuityRothAccountId'
              size='small'
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid='cancelEditAnnuityPolicy'
            onClick={props.onClose}
            variant='outlined'>
            Cancel
          </Button>
          <FormButton
            data-testid='submitEditAnnuityPolicy'
            disabled={patchPolicy.isPending}
            variant='contained'>
            Save
          </FormButton>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

EnrollmentAnnuityPolicyEditDialog.displayName =
  'EnrollmentAnnuityPolicyEditDialog';
